/**
 * PagerDutyCard — admin panel on /admin/email-config that surfaces PagerDuty
 * integration status + the 24h page audit log + a "send test page" button.
 * Graceful when the routing key is unset (shows setup instructions).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { AlertTriangle, CheckCircle, Zap, RefreshCw, Send } from 'lucide-react';
import IncidentRoutingCard from './IncidentRoutingCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_COLORS = {
  critical: '#FF3B30',
  error:    '#FF9500',
  warning:  '#FFCC00',
  info:     '#007AFF',
};

const PagerDutyCard = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [showOpenOnly, setShowOpenOnly] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/admin/pagerduty/status`, { headers }),
        axios.get(`${API}/admin/pagerduty/events?limit=20`, { headers }),
      ]);
      setStatus(s.data);
      setEvents(e.data.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load PagerDuty status');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/pagerduty/test`, {}, { headers });
      if (r.data.delivered) {
        toast.success('Test page delivered and auto-resolved. Check PagerDuty.');
      } else if (r.data.configured) {
        toast.error('Routing key is configured but PagerDuty rejected the event. Check backend logs.');
      } else {
        toast.info('Routing key not set — test logged as skipped. Configure PAGERDUTY_ROUTING_KEY to go live.');
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test page failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading && !status) return <Card className="border-[#E5E7EB]"><CardContent className="p-6 text-sm text-[#9CA3AF]">Loading PagerDuty status…</CardContent></Card>;
  if (!status) return null;

  const s24 = status.summary_24h || {};
  const configured = status.configured;

  return (
    <Card className="border-[#E5E7EB]" data-testid="pagerduty-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (configured ? '#34C759' : '#9CA3AF') + '15' }}>
              <Zap className="w-4 h-4" style={{ color: configured ? '#34C759' : '#9CA3AF' }} />
            </div>
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                Incident paging
                {status.pagerduty_configured
                  ? <Badge className="text-[9px] bg-[#06AC38]/10 text-[#06AC38]">PagerDuty</Badge>
                  : <Badge variant="secondary" className="text-[9px] opacity-50">PagerDuty</Badge>}
                {status.opsgenie_configured
                  ? <Badge className="text-[9px] bg-[#172B4D]/10 text-[#172B4D]">Opsgenie</Badge>
                  : <Badge variant="secondary" className="text-[9px] opacity-50">Opsgenie</Badge>}
                {!configured && <Badge className="text-[9px] bg-[#9CA3AF]/10 text-[#9CA3AF]">Dry-run</Badge>}
              </CardTitle>
              <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                High-severity KT signals, digest-email bounce spikes, and SSO connection deletions page on-call. Events fan out to every configured provider.
              </p>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={fetchAll} data-testid="refresh-pagerduty-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" className="h-7 text-xs bg-[#FF9500] hover:bg-[#FF9500]/90" onClick={sendTest} disabled={testing} data-testid="test-pagerduty-btn">
              <Send className="w-3 h-3 mr-1" />
              {testing ? 'Sending…' : 'Send test'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!configured && (
          <div className="flex items-start gap-2 p-2 bg-[#FF9500]/5 border border-[#FF9500]/20 rounded text-[11px] text-[#4B5563]">
            <AlertTriangle className="w-3 h-3 text-[#FF9500] flex-shrink-0 mt-0.5" />
            <div>
              <p><strong>Dry-run mode.</strong> No incident provider is configured. In <code className="bg-white px-1 rounded">backend/.env</code>, set <code className="bg-white px-1 rounded">PAGERDUTY_ROUTING_KEY</code> to a PagerDuty Events API v2 integration key, and/or <code className="bg-white px-1 rounded">OPSGENIE_API_KEY</code> to an Opsgenie API integration key (set <code className="bg-white px-1 rounded">OPSGENIE_REGION=eu</code> for EU accounts). Restart the backend. The audit log below records every attempt — including dry-runs — so you can see what <em>would</em> have paged on-call.</p>
            </div>
          </div>
        )}

        {/* Open-incidents strip — triggers with no matching resolve */}
        <div className={`p-3 border rounded ${status.open_incidents_count > 0 ? 'border-[#FF3B30]/30 bg-[#FF3B30]/5' : 'border-[#34C759]/30 bg-[#34C759]/5'}`} data-testid="open-incidents-strip">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {status.open_incidents_count > 0
                ? <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                : <CheckCircle className="w-4 h-4 text-[#34C759]" />}
              <div>
                <p className="text-sm font-semibold" style={{ color: status.open_incidents_count > 0 ? '#FF3B30' : '#34C759' }}>
                  {status.open_incidents_count === 0
                    ? 'No open incidents'
                    : `${status.open_incidents_count} open incident${status.open_incidents_count === 1 ? '' : 's'}`}
                </p>
                <p className="text-[10px] text-[#9CA3AF]">
                  {status.open_incidents_count > 0
                    ? 'Triggers with no matching resolve — would currently be paging on-call.'
                    : 'Every trigger in the log has a matching resolve. Rotation is clean.'}
                </p>
              </div>
            </div>
            {status.open_incidents_count > 0 && (
              <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0" onClick={() => setShowOpenOnly(!showOpenOnly)} data-testid="toggle-open-only-btn">
                {showOpenOnly ? 'Show all' : 'Show open only'}
              </Button>
            )}
          </div>
          {showOpenOnly && status.open_incidents_count > 0 && (
            <div className="mt-3 space-y-1 max-h-[200px] overflow-y-auto" data-testid="open-incidents-list">
              {(status.open_incidents || []).map(inc => (
                <div key={inc.dedup_key} className="flex items-center gap-2 p-1.5 bg-white rounded border border-[#FF3B30]/20 text-[11px]">
                  <Badge className="text-[9px] font-semibold" style={{ backgroundColor: (SEVERITY_COLORS[inc.severity] || '#4B5563') + '15', color: SEVERITY_COLORS[inc.severity] || '#4B5563' }}>
                    {inc.severity?.toUpperCase() || 'INFO'}
                  </Badge>
                  <span className="text-[#4B5563] truncate flex-1">{inc.summary}</span>
                  <span className="text-[10px] text-[#9CA3AF]">{inc.triggered_at?.slice(5, 16).replace('T', ' ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 24h KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Pages (24h)</p><p className="font-semibold">{s24.total || 0}</p></div>
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Delivered</p><p className="font-semibold text-[#34C759]">{s24.delivered || 0}</p></div>
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Dry-runs</p><p className="font-semibold text-[#9CA3AF]">{s24.skipped || 0}</p></div>
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Critical</p><p className="font-semibold" style={{ color: SEVERITY_COLORS.critical }}>{s24.by_severity_critical || 0}</p></div>
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Warning</p><p className="font-semibold" style={{ color: SEVERITY_COLORS.warning }}>{s24.by_severity_warning || 0}</p></div>
        </div>

        {/* Per-event-type routing rules */}
        <IncidentRoutingCard
          pagerDutyConfigured={!!status.pagerduty_configured}
          opsgenieConfigured={!!status.opsgenie_configured}
        />

        {/* Recent events */}
        <div>
          <h4 className="text-xs font-semibold mb-2">Recent events</h4>
          {events.length === 0 ? (
            <p className="text-[11px] text-[#9CA3AF]">No PagerDuty activity yet. Trigger a test page above, or wait for a high-severity signal to fire.</p>
          ) : (
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {events.map(e => (
                <div key={e.event_id} className="flex items-center gap-2 p-2 border border-[#E5E7EB] rounded text-[11px]" data-testid={`pd-event-${e.event_id}`}>
                  <Badge className="text-[9px] font-semibold" style={{ backgroundColor: (SEVERITY_COLORS[e.severity] || '#4B5563') + '15', color: SEVERITY_COLORS[e.severity] || '#4B5563' }}>
                    {e.severity.toUpperCase()}
                  </Badge>
                  <span className="text-[10px] text-[#9CA3AF] w-14 flex-shrink-0">{e.action}</span>
                  {e.provider && (
                    <Badge variant="secondary" className="text-[9px] flex-shrink-0" title={`Sent via ${e.provider}`}>
                      {e.provider === 'pagerduty' ? 'PD' : e.provider === 'opsgenie' ? 'OG' : e.provider}
                    </Badge>
                  )}
                  <span className="text-[#4B5563] truncate flex-1">{e.summary}</span>
                  {e.skipped
                    ? <Badge variant="secondary" className="text-[9px]">dry-run</Badge>
                    : e.delivered
                      ? <Badge className="text-[9px] bg-[#34C759]/10 text-[#34C759]">delivered</Badge>
                      : <Badge className="text-[9px] bg-[#FF3B30]/10 text-[#FF3B30]">failed</Badge>}
                  <span className="text-[10px] text-[#9CA3AF] hidden md:inline">{e.created_at?.slice(5, 16).replace('T', ' ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PagerDutyCard;
